import { useEffect, useMemo, useRef, useState } from "react"
import { ArrowLeft, Check, ChevronDown, ChevronRight } from "lucide-react"
import { cn } from "../../lib/utils"
import type { AgentDescriptor, AgentKey } from "./types"

interface AgentSwitchPopoverProps {
  agents: AgentDescriptor[]
  activeKey: AgentKey
  onSelect: (key: AgentKey) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Active agent — used to render the trigger pill. */
  activeAgent: AgentDescriptor
  /** Class on the OUTER wrapper (the flex item itself). Use for
   * vertical alignment overrides like `self-center` so the badge
   * lines up with sibling textarea / button cluster in the
   * composer row. */
  wrapperClassName?: string
  triggerClassName?: string
  /** Side the menu opens to relative to the trigger. The agent
   * switcher in the composer wants `top` (menu above the pill);
   * leaving the option here so other consumers can flip. */
  side?: "top" | "bottom"
}

/**
 * Hand-rolled controlled dropdown — replaces an earlier base-ui
 * `<Popover>` integration that was wrapping the trigger in a Slot
 * pattern and bleeding click area into neighbouring composer
 * buttons (paperclip / send unclickable on their left edge).
 *
 * Anchors via `position: relative` on the trigger button + an
 * `absolute` menu next to it. No portal — the menu lives inside
 * the panel's stacking context and inherits its CSS variables
 * (no `new-ui-theme` / `var(--popover)` plumbing required).
 */
export function AgentSwitchPopover({
  agents,
  activeKey,
  onSelect,
  open,
  onOpenChange,
  activeAgent,
  wrapperClassName,
  triggerClassName,
  side = "top",
}: AgentSwitchPopoverProps) {
  const isPreview = !activeAgent.available
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  // When set, we're inside a category's drill-down view —
  // popover shows that parent's children instead of the top
  // level. Reset to null on close so reopening always lands
  // back at the top.
  const [drillKey, setDrillKey] = useState<AgentKey | null>(null)
  useEffect(() => {
    if (!open) setDrillKey(null)
  }, [open])

  // Hierarchy maths: top-level entries are agents without a
  // parent; children-by-parent groups the rest under their
  // declared `parentKey`. Computed once per `agents` change so
  // the popover doesn't recompute on every render.
  const { topLevel, childrenByParent } = useMemo(() => {
    const top: AgentDescriptor[] = []
    const map = new Map<AgentKey, AgentDescriptor[]>()
    for (const a of agents) {
      if (a.parentKey) {
        const arr = map.get(a.parentKey) ?? []
        arr.push(a)
        map.set(a.parentKey, arr)
      } else {
        top.push(a)
      }
    }
    return { topLevel: top, childrenByParent: map }
  }, [agents])

  const drilledParent = drillKey
    ? agents.find((a) => a.key === drillKey) ?? null
    : null
  const drilledChildren = drillKey ? childrenByParent.get(drillKey) ?? [] : []

  // Click-outside / Esc to close. Pointerdown (not click) so we
  // catch the down event before any sibling element gets a click
  // — keeps closing snappy. Esc inside a drill view first goes
  // back one level, then closes on a second press.
  useEffect(() => {
    if (!open) return
    const onPointer = (e: PointerEvent) => {
      const root = wrapperRef.current
      if (!root) return
      if (!(e.target instanceof Node)) return
      if (root.contains(e.target)) return
      onOpenChange(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      if (drillKey) {
        setDrillKey(null)
      } else {
        onOpenChange(false)
      }
    }
    window.addEventListener("pointerdown", onPointer, true)
    window.addEventListener("keydown", onKey)
    return () => {
      window.removeEventListener("pointerdown", onPointer, true)
      window.removeEventListener("keydown", onKey)
    }
  }, [open, onOpenChange, drillKey])

  return (
    <div
      ref={wrapperRef}
      className={cn("relative inline-flex", wrapperClassName)}
    >
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        data-slot="agent-badge"
        aria-haspopup="listbox"
        aria-expanded={open || undefined}
        aria-label={`Agent: ${activeAgent.label}${isPreview ? " (preview)" : ""}`}
        className={cn(
          "inline-flex h-6 items-center gap-1 rounded-full border px-2 text-[0.7rem] font-medium",
          "border-black/10 bg-background text-foreground hover:bg-foreground/5",
          "dark:border-white/10",
          "transition-colors",
          isPreview && "opacity-60",
          triggerClassName,
        )}
      >
        <span
          className="size-1.5 rounded-full bg-primary"
          aria-hidden="true"
        />
        <span className="truncate">{activeAgent.label}</span>
        {isPreview && (
          <span className="text-muted-foreground">preview</span>
        )}
        <ChevronDown className="size-3 opacity-60" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Switch agent"
          className={cn(
            "absolute left-0 z-50 w-64 rounded-lg border bg-popover p-1 text-popover-foreground shadow-lg",
            "border-black/10 dark:border-white/10",
            side === "top"
              ? "bottom-full mb-1.5"
              : "top-full mt-1.5",
            "animate-in fade-in-0 zoom-in-95 duration-100",
          )}
        >
          {drillKey && drilledParent ? (
            // Drill-down view — back button + parent label
            // header + the parent itself ("General <category>")
            // and all children as selectable rows.
            <>
              <div className="flex items-center gap-1.5 border-b border-black/5 px-1.5 pb-1 pt-0.5 dark:border-white/5">
                <button
                  type="button"
                  onClick={() => setDrillKey(null)}
                  aria-label="Back to all categories"
                  className="rounded p-0.5 text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                >
                  <ArrowLeft className="size-3.5" aria-hidden="true" />
                </button>
                <span className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted-foreground">
                  {drilledParent.label}
                </span>
              </div>
              <ul className="flex flex-col">
                {[
                  // Parent itself appears first, labelled
                  // "General <category>" so it reads as the
                  // catch-all option inside the group.
                  {
                    ...drilledParent,
                    label: `General ${drilledParent.label.toLowerCase()}`,
                  },
                  ...drilledChildren,
                ].map((a) => (
                  <AgentRow
                    key={a.key}
                    agent={a}
                    isActive={a.key === activeKey}
                    onSelect={() => {
                      if (!a.available) return
                      onSelect(a.key)
                      onOpenChange(false)
                    }}
                  />
                ))}
              </ul>
            </>
          ) : (
            // Top-level view — single agents click to select;
            // categories with sub-agents drill down on click.
            <ul className="flex flex-col">
              {topLevel.map((a) => {
                const children = childrenByParent.get(a.key) ?? []
                const hasChildren = children.length > 0
                const isActive =
                  a.key === activeKey ||
                  children.some((c) => c.key === activeKey)
                const disabled = !a.available && !hasChildren
                return (
                  <li key={a.key}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={a.key === activeKey}
                      aria-disabled={disabled || undefined}
                      disabled={disabled}
                      onClick={() => {
                        if (hasChildren) {
                          setDrillKey(a.key)
                          return
                        }
                        if (disabled) return
                        onSelect(a.key)
                        onOpenChange(false)
                      }}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
                        "focus:outline-none",
                        disabled
                          ? "cursor-not-allowed opacity-40"
                          : "hover:bg-foreground/5 focus:bg-foreground/5",
                      )}
                    >
                      <span
                        className={cn(
                          "size-1.5 shrink-0 rounded-full",
                          disabled
                            ? "bg-muted-foreground/40"
                            : "bg-primary",
                        )}
                        aria-hidden="true"
                      />
                      <span
                        className={cn(
                          "min-w-0 flex-1 truncate",
                          disabled && "text-muted-foreground",
                        )}
                      >
                        {a.label}
                      </span>
                      {disabled && (
                        <span className="text-[0.7rem] text-muted-foreground">
                          soon
                        </span>
                      )}
                      {hasChildren ? (
                        <ChevronRight
                          className="size-3.5 opacity-60"
                          aria-hidden="true"
                        />
                      ) : isActive && !disabled ? (
                        <Check
                          className="size-3.5 text-primary"
                          aria-hidden="true"
                        />
                      ) : null}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Single selectable agent row used inside the drill-down view.
 * Same layout as the top-level rows for visual rhythm — dot,
 * label, optional "soon" pill, optional check on the active
 * pick.
 */
function AgentRow({
  agent,
  isActive,
  onSelect,
}: {
  agent: AgentDescriptor
  isActive: boolean
  onSelect: () => void
}) {
  const disabled = !agent.available
  return (
    <li>
      <button
        type="button"
        role="option"
        aria-selected={isActive}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        onClick={onSelect}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
          "focus:outline-none",
          disabled
            ? "cursor-not-allowed opacity-40"
            : "hover:bg-foreground/5 focus:bg-foreground/5",
        )}
      >
        <span
          className={cn(
            "size-1.5 shrink-0 rounded-full",
            disabled ? "bg-muted-foreground/40" : "bg-primary",
          )}
          aria-hidden="true"
        />
        <span
          className={cn(
            "min-w-0 flex-1 truncate",
            disabled && "text-muted-foreground",
          )}
        >
          {agent.label}
        </span>
        {disabled && (
          <span className="text-[0.7rem] text-muted-foreground">soon</span>
        )}
        {isActive && !disabled && (
          <Check className="size-3.5 text-primary" aria-hidden="true" />
        )}
      </button>
    </li>
  )
}
